import {
  normalizeImportedAssetFileId,
  parseImportedTopoSidecar,
  type ImportedTopoSidecarDocument,
  type ImportedTopoSidecarEntry,
} from '../sidecar'
import type {
  ImportedPartBodyBrepBinding,
  ImportedTopologySource,
} from '../loaders/types'
import type {
  ImportedAssetFileDescriptor,
  ImportedTopologySourceBrepHydrationOptions,
} from './hydrateImportedTopologySourceBrepRuntime'
import { createImportedBrepRuntimeCache, type ImportedBrepRuntimeCache } from './ImportedBrepRuntimeCache'
import {
  findImportedSidecarFile,
  resolveImportedTopologySidecarFileId,
} from './imported-sidecar-file-lookup'
import { formatImportedTopologyHydrationDiagnostic } from './imported-topology-hydration-diagnostics'

/**
 * topo sidecar 文本解码器。
 */
const importedTopoSidecarTextDecoder = new TextDecoder()

/**
 * topo sidecar hydration 结果。
 */
export interface ImportedTopoHydrationResult {
  /** 等待 BRep hydration 全部成功后统一提交的 body 拓扑资产。 */
  bodyTopologyAssets: NonNullable<ImportedTopologySource['bodyTopologyAssets']>
  /** 已通过 topo sidecar 成功补齐的 body 绑定 ID。 */
  hydratedBodyBindingIds: Set<string>
  /** topo sidecar 不可用的 body 绑定 ID 与失败原因。 */
  unavailableReasonsByBodyBindingId: Map<string, string>
}

/**
 * topo sidecar entry 命中结果。
 */
interface ImportedTopoEntryResolution {
  /** 命中的 topo sidecar entry。 */
  entry: ImportedTopoSidecarEntry | null
  /** 未命中时的具体原因。 */
  failureReason?: string
}

/**
 * topo sidecar body 绑定分组结果。
 */
interface ImportedTopoBindingGroupingResult {
  /** 按 sidecar 文件 ID 分组后的 body 绑定。 */
  groupedBindings: Map<string, ImportedPartBodyBrepBinding[]>
  /** 分组阶段已经确定 topo sidecar 不可用的原因。 */
  unavailableReasonsByBodyBindingId: Map<string, string>
}

/**
 * 获取运行时缓存。
 *
 * @param importedBrepRuntimeCache 外部注入缓存。
 * @returns 可用缓存实例。
 */
function resolveImportedTopoRuntimeCache(
  importedBrepRuntimeCache: ImportedBrepRuntimeCache | undefined,
): ImportedBrepRuntimeCache {
  return importedBrepRuntimeCache ?? createImportedBrepRuntimeCache()
}

/**
 * 触发可选的当前请求断言。
 *
 * @param assertCurrent 当前请求断言函数。
 */
function assertImportedTopoHydrationCurrent(assertCurrent?: () => void): void {
  assertCurrent?.()
}

/**
 * 记录 topo sidecar warning。
 *
 * @param options hydration 选项。
 * @param message 日志内容。
 */
function reportImportedTopoHydrationWarning<TFile extends ImportedAssetFileDescriptor>(
  options: ImportedTopologySourceBrepHydrationOptions<TFile>,
  message: string,
): void {
  options.logger?.onWarn?.(message)
}

/**
 * 读取并解析 topo sidecar 文档。
 *
 * @param sidecarFile sidecar 文件。
 * @param runtimeCache 运行时缓存。
 * @param readFileBytes 文件读取器。
 * @returns topo sidecar 文档。
 */
async function loadImportedTopoSidecarDocument<TFile extends ImportedAssetFileDescriptor>(
  sidecarFile: TFile,
  runtimeCache: ImportedBrepRuntimeCache,
  readFileBytes: (file: TFile) => Promise<Uint8Array>,
): Promise<ImportedTopoSidecarDocument> {
  const normalizedSidecarFileId = normalizeImportedAssetFileId(sidecarFile.fileId)
  return runtimeCache.getOrCreateTopoSidecarDocument(normalizedSidecarFileId, async () => {
    const sidecarBytes = await readFileBytes(sidecarFile)
    return parseImportedTopoSidecar(importedTopoSidecarTextDecoder.decode(sidecarBytes))
  })
}

/**
 * 按 body 绑定分组 topo sidecar 候选。
 *
 * @param file 当前 GLB 文件。
 * @param topologySource 导入拓扑源。
 * @param options hydration 选项。
 * @returns sidecar 文件 ID 到 body 绑定的分组，以及分组阶段产生的 topo 不可用原因。
 */
function groupImportedTopoBindingsBySidecarFileId<TFile extends ImportedAssetFileDescriptor>(
  file: TFile,
  topologySource: ImportedTopologySource,
  options: ImportedTopologySourceBrepHydrationOptions<TFile>,
): ImportedTopoBindingGroupingResult {
  const groupedBindings = new Map<string, ImportedPartBodyBrepBinding[]>()
  const unavailableReasonsByBodyBindingId = new Map<string, string>()
  const existingBodyTopologyAssets: NonNullable<ImportedTopologySource['bodyTopologyAssets']> =
    topologySource.bodyTopologyAssets ?? {}
  const bodyBindings = Object.values(topologySource.bodyBrepBindings ?? {})
    .filter((binding) => !existingBodyTopologyAssets[binding.bodyBindingId])

  bodyBindings.forEach((binding) => {
    if (!binding.topologyRef) {
      return
    }

    const sidecarFileId = resolveImportedTopologySidecarFileId(file.fileId, binding.topologyRef)
    const sidecarFile = findImportedSidecarFile(options.projectFiles, sidecarFileId)
    if (!sidecarFile) {
      const unavailableReason = `topo sidecar 文件未找到: ${sidecarFileId}`
      unavailableReasonsByBodyBindingId.set(binding.bodyBindingId, unavailableReason)
      reportImportedTopoHydrationWarning(
        options,
        `topo sidecar 不可用，当前 body 不会生成 Feature 拓扑: ${formatImportedTopologyHydrationDiagnostic({
          file,
          topologySource,
          binding,
          reason: unavailableReason,
          topologySidecarFileId: sidecarFileId,
        })}`,
      )
      return
    }

    const bucket = groupedBindings.get(sidecarFileId)
    if (bucket) {
      bucket.push(binding)
      return
    }
    groupedBindings.set(sidecarFileId, [binding])
  })

  return {
    groupedBindings,
    unavailableReasonsByBodyBindingId,
  }
}

/**
 * 判断 topo sidecar entry 与 GLB 显式引用的差异原因。
 *
 * @param entry topo sidecar entry。
 * @param binding body 绑定。
 * @returns 不匹配原因；匹配时返回 `null`。
 */
function resolveImportedTopoEntryMismatchReason(
  entry: ImportedTopoSidecarEntry,
  binding: ImportedPartBodyBrepBinding,
): string | null {
  const topologyRef = binding.topologyRef
  if (!topologyRef) {
    return '当前 body 未声明 topologyRef'
  }

  if (entry.key !== topologyRef.key) {
    return `topo sidecar entry key 不匹配: expected=${topologyRef.key}; actual=${entry.key}`
  }
  if (topologyRef.sha256 && entry.sha256 !== topologyRef.sha256) {
    return `topo sidecar entry sha256 不匹配: expected=${topologyRef.sha256}; actual=${entry.sha256 ?? '缺失'}`
  }
  if (typeof topologyRef.byteLength === 'number'
    && entry.byteLength !== topologyRef.byteLength) {
    return `topo sidecar entry byteLength 不匹配: expected=${topologyRef.byteLength}; actual=${
      typeof entry.byteLength === 'number' ? entry.byteLength : '缺失'
    }`
  }
  return null
}

/**
 * 为 body 绑定选择显式引用的交互 topo entry。
 *
 * 设计说明：
 * 1. topo sidecar 必须由 GLB `topologyRef.uri + topologyRef.key` 精确定位；
 * 2. 这里不再按 bodyId、单 entry 或文件名做推导，避免错误 sidecar 静默命中后污染 FeatureFace/Line/Point；
 * 3. entry 校验失败时返回结构化原因，上层只记录协议错误，不再使用 BRep 反向生成 Feature 拓扑。
 *
 * @param document topo sidecar 文档。
 * @param binding body 绑定。
 * @returns 命中的 topo entry。
 */
function resolveImportedTopoEntryForBinding(
  document: ImportedTopoSidecarDocument,
  binding: ImportedPartBodyBrepBinding,
): ImportedTopoEntryResolution {
  if (!binding.topologyRef) {
    return {
      entry: null,
      failureReason: '当前 body 未声明 topologyRef',
    }
  }

  const refMatchedEntry = document.entries[binding.topologyRef.key]
  if (!refMatchedEntry) {
    return {
      entry: null,
      failureReason: `topo sidecar entry 未找到: ${binding.topologyRef.key}`,
    }
  }

  const mismatchReason = resolveImportedTopoEntryMismatchReason(refMatchedEntry, binding)
  return mismatchReason
    ? {
      entry: null,
      failureReason: mismatchReason,
    }
    : {
      entry: refMatchedEntry,
    }
}

/**
 * 构建 topo sidecar meshData 缓存键。
 *
 * @param sidecarFileId sidecar 文件 ID。
 * @param entry topo sidecar entry。
 * @returns 缓存键。
 */
function buildImportedTopoMeshDataCacheKey(sidecarFileId: string, entry: ImportedTopoSidecarEntry): string {
  return [
    'topo-sidecar',
    normalizeImportedAssetFileId(sidecarFileId),
    entry.key,
    entry.sha256 ?? '',
    String(entry.byteLength ?? ''),
  ].join(':')
}

/**
 * 通过 topo sidecar 快速补齐导入拓扑运行时。
 *
 * 设计说明：
 * 1. topo sidecar 已经是后端网格化后的交互拓扑快照，可直接提供 pick meshData 与 topologySnapshot；
 * 2. 本函数只构建暂存拓扑，不写回源对象；外层 BRep hydration 会在真实 Shape 全部注册成功后统一提交；
 * 3. GLB 可见网格 face range 只来自 primitive `__topo_face_ranges`，避免把 body 级 sidecar 范围误用到单个 host；
 * 4. topo sidecar 不存在或 entry 不匹配时记录 body 级失败原因，BRep 阶段只负责导出 Shape 注册。
 *
 * @param file 当前 GLB 文件。
 * @param topologySource 目标导入拓扑源。
 * @param options 运行时依赖。
 */
export async function hydrateImportedTopologySourceTopoRuntime<TFile extends ImportedAssetFileDescriptor>(
  file: TFile,
  topologySource: ImportedTopologySource,
  options: ImportedTopologySourceBrepHydrationOptions<TFile>,
): Promise<ImportedTopoHydrationResult> {
  const groupingResult = groupImportedTopoBindingsBySidecarFileId(file, topologySource, options)
  const bodyTopologyAssets: NonNullable<ImportedTopologySource['bodyTopologyAssets']> = {
    ...(topologySource.bodyTopologyAssets ?? {}),
  }
  const result: ImportedTopoHydrationResult = {
    bodyTopologyAssets,
    hydratedBodyBindingIds: new Set<string>(),
    unavailableReasonsByBodyBindingId: new Map(groupingResult.unavailableReasonsByBodyBindingId),
  }
  const { groupedBindings } = groupingResult
  if (groupedBindings.size === 0) {
    return result
  }

  const runtimeCache = resolveImportedTopoRuntimeCache(options.importedBrepRuntimeCache)
  for (const [sidecarFileId, sidecarBindings] of groupedBindings.entries()) {
    assertImportedTopoHydrationCurrent(options.assertCurrent)
    const sidecarFile = findImportedSidecarFile(options.projectFiles, sidecarFileId)
    if (!sidecarFile) {
      continue
    }

    let sidecarDocument: ImportedTopoSidecarDocument
    try {
      sidecarDocument = await loadImportedTopoSidecarDocument(sidecarFile, runtimeCache, options.readFileBytes)
    } catch (error) {
      const unavailableReason = `topo sidecar 读取失败: ${error instanceof Error ? error.message : String(error)}`
      sidecarBindings.forEach((binding) => {
        result.unavailableReasonsByBodyBindingId.set(binding.bodyBindingId, unavailableReason)
        reportImportedTopoHydrationWarning(
          options,
          `topo sidecar 不可用，当前 body 不会生成 Feature 拓扑: ${formatImportedTopologyHydrationDiagnostic({
            file,
            topologySource,
            binding,
            reason: unavailableReason,
            topologySidecarFileId: sidecarFileId,
          })}`,
        )
      })
      continue
    }
    assertImportedTopoHydrationCurrent(options.assertCurrent)

    for (const binding of sidecarBindings) {
      assertImportedTopoHydrationCurrent(options.assertCurrent)
      const entryResolution = resolveImportedTopoEntryForBinding(sidecarDocument, binding)
      const { entry } = entryResolution
      if (!entry) {
        const unavailableReason = entryResolution.failureReason ?? 'topo sidecar entry 未命中'
        result.unavailableReasonsByBodyBindingId.set(binding.bodyBindingId, unavailableReason)
        reportImportedTopoHydrationWarning(
          options,
          `topo sidecar 不可用，当前 body 不会生成 Feature 拓扑: ${formatImportedTopologyHydrationDiagnostic({
            file,
            topologySource,
            binding,
            reason: unavailableReason,
            topologySidecarFileId: sidecarFileId,
          })}`,
        )
        continue
      }

      if (entry.payload.diagnostics.topologyFaceRangeMismatchCount > 0) {
        reportImportedTopoHydrationWarning(
          options,
          `topo sidecar face range 与 meshData 不一致，已按 meshData 修正拾取范围: ${sidecarFileId}#${entry.key}`,
        )
      }

      const meshDataCacheKey = buildImportedTopoMeshDataCacheKey(sidecarFileId, entry)
      const topoMeshData = await runtimeCache.getOrCreateMeshData(meshDataCacheKey, async () => entry.payload.meshData)
      bodyTopologyAssets[binding.bodyBindingId] = {
        bodyBindingId: binding.bodyBindingId,
        meshData: {
          ...topoMeshData,
          metadata: {
            ...(topoMeshData.metadata ?? {}),
            bodyBindingId: binding.bodyBindingId,
            bodyId: binding.bodyId,
            meshId: binding.meshId,
            sourceFileId: file.fileId,
            sourceFileUuid: file.uuid,
            sourceLevel: binding.sourceLevel,
          },
        },
      }
      result.hydratedBodyBindingIds.add(binding.bodyBindingId)
    }
  }

  return result
}
